import { MARKETING_SITE_ANALYTICS_ID } from "./constants";
import { recordWebsiteAnalytics } from "./recordWebsiteAnalytics";

function resolveSiteId(): string {
  const fromEnv = import.meta.env.VITE_MARKETING_ANALYTICS_SITE_ID as string | undefined;
  return fromEnv?.trim() || MARKETING_SITE_ANALYTICS_ID;
}

const visitorKeyFor = (siteId: string) => `wnp_v_${siteId}`;
const sessionKeyFor = (siteId: string) => `wnp_s_${siteId}`;

function visitorId(siteId: string): string {
  const vk = visitorKeyFor(siteId);
  try {
    let v = localStorage.getItem(vk);
    if (!v) {
      v = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(vk, v);
    }
    return v;
  } catch {
    return "anon";
  }
}

function sessionInfo(siteId: string): { id: string; isNew: boolean } {
  const sk = sessionKeyFor(siteId);
  try {
    const existing = sessionStorage.getItem(sk);
    if (existing) return { id: existing, isNew: false };
    const id = Math.random().toString(36).slice(2);
    sessionStorage.setItem(sk, id);
    return { id, isNew: true };
  } catch {
    return { id: "sess", isNew: true };
  }
}

let unloadHookInstalled = false;
let pageStartedAt = Date.now();
let activeSiteId = resolveSiteId();
let activeSession = sessionInfo(activeSiteId);

function installUnloadHook(): void {
  if (unloadHookInstalled || typeof window === "undefined") return;
  unloadHookInstalled = true;
  window.addEventListener("pagehide", () => {
    const durationSeconds = Math.round((Date.now() - pageStartedAt) / 1000);
    recordWebsiteAnalytics(
      {
        rental_website_id: activeSiteId,
        visitor_id: visitorId(activeSiteId),
        session_id: activeSession.id,
        is_new_session: false,
        event_type: "duration",
        duration_seconds: durationSeconds,
      },
      { keepalive: true },
    );
  });
}

/** Traffic-source bucketing needs only the referrer's hostname — never send the full URL
 * (query strings on the referring page can carry the visitor's own search terms etc). */
function referrerHost(): string | null {
  try {
    return document.referrer ? new URL(document.referrer).hostname : null;
  } catch {
    return null;
  }
}

/**
 * Pošle pageview pri návšteve / zmene route na marketing webe.
 * referrer/UTM sa posielajú LEN pri novej relácii — traffic source patrí k reláciám, nie
 * k jednotlivým zobrazeniam; pri druhej a ďalšej stránke tej istej relácie by referrer
 * ukazoval na internú predchádzajúcu stránku, nie na skutočný zdroj návštevy.
 */
export function trackMarketingPageView(path: string): void {
  if (typeof window === "undefined") return;
  if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY) return;

  activeSiteId = resolveSiteId();
  activeSession = sessionInfo(activeSiteId);
  pageStartedAt = Date.now();
  installUnloadHook();

  const params = new URLSearchParams(window.location.search);

  recordWebsiteAnalytics({
    rental_website_id: activeSiteId,
    visitor_id: visitorId(activeSiteId),
    session_id: activeSession.id,
    is_new_session: activeSession.isNew,
    event_type: "pageview",
    duration_seconds: 0,
    path,
    viewport_width: window.innerWidth,
    ...(activeSession.isNew && {
      referrer: referrerHost(),
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
    }),
  });
}
